// What a game's page and the list pages say, for both of their writers:
// app.js, in the browser, and the Worker (../src/index.ts), which writes those
// pages into index.html for readers that don't run app.js. It's one copy
// so the two can't drift: a crawler that runs app.js and one that doesn't read
// the same title, description and sentences. Nothing here touches the DOM.
//
// The game page is here; its parts and the other pages are in modules of
// their own; figures.ts's setFormats sets the formats they all write in.
// Import each name from the module that owns it:
//
//   markup.ts       names, art, links, section heads, tabs, the pager
//   figures.ts      how counts, changes and places are written (Formats)
//   routes.ts       the URLs, and the API request behind each page
//   meta.ts         every page's head tags, and the not-found pages
//   tag-pages.ts    which tag pages there are, and what each says
//   store-facts.ts  the US price and the store's details
//   updates.ts      a game's updates, listed and weighed
//   chart-panel.ts  the chart's stats, range tabs and toggles
//   rows.ts         list rows, badges and cards of games
//   list-pages.ts   the home list, tags index, tag, company and search pages

import type { AppResponse, HistoryRange } from "../../../shared/api.ts";
import { AROUND_THE_CLOCK, EVENINGS, type Region, playTimes } from "../../../shared/evenings.ts";
import { COMPANY_ROLES } from "../../../shared/companies.ts";
import { companyPath, tagPagePath } from "../../../shared/site.ts";
import { chartPanel, playTimeCard } from "../chart/chart-panel.ts";
import { changeNote, clock, date, hour, number, peakShare, percentPlain, rankMove } from "../figures.ts";
import { art, displayName, externalLink, pageHref, plural } from "../markup.ts";
import { EMBED_PATH, PER_PAGE, SITE, storeUrl } from "../routes.ts";
import { card, cardGames, moverBadges } from "../rows.ts";
import { storeFacts } from "../store-facts.ts";
import { DEFAULT_UPDATE_KINDS, updateCard, updateList } from "../updates.ts";
import { dayChange, escapeHtml } from "../web.ts";

// The iframe tag another site pastes to show a game's card. Rounded and
// borderless here, as the card draws its own border.
export function embedCode(appId: number, name: string): string {
  return `<iframe src="${SITE}${EMBED_PATH}?app=${appId}" title="${escapeHtml(`${name} player count · steamdata.dev`)}" width="100%" height="150" style="max-width:420px;border:0;border-radius:12px" loading="lazy"></iframe>`;
}

// A game page's way to embed its card: closed until opened, so its preview
// (an iframe of this site's own card) loads only then. Its Copy button is
// app.js's.
export function embedPanel(appId: number, name: string): string {
  return `
      <details class="panel embed-panel">
        <summary>Embed ${escapeHtml(name)}'s player count on your site</summary>
        <div class="embed-body">
          <iframe class="embed-preview" src="${EMBED_PATH}?app=${appId}" title="Preview of the card"
            height="150" loading="lazy"></iframe>
          <div class="embed-code">
            <textarea readonly rows="4" spellcheck="false" aria-label="Embed code">${escapeHtml(embedCode(appId, name))}</textarea>
            <button type="button" class="embed-copy">Copy code</button>
            <p class="muted">The card follows the reader's light or dark setting. Add
              <code>&amp;theme=light</code> or <code>&amp;theme=dark</code> after the app id to fix it.</p>
          </div>
        </div>
      </details>`;
}

// The game page's cards of other games by its developers and publishers
// (the API's `companies`), most played first, each company's titled with
// its name and linking to its page. A company that's both is one card,
// under the role it has here first (developer); one with no other ranked
// game gets none. Nothing when no company has one.
function companyCards(game: AppResponse): string {
  const companies = game.companies ?? [];
  const shown = new Set<string>();
  const cards: string[] = [];

  for (const { role, slug, name, total, apps } of companies) {
    if (shown.has(slug) || !apps.length) continue;
    shown.add(slug);

    // Its roles here, in the API's order (developer first).
    const as = companies
      .filter((c) => c.slug === slug)
      .map((c, i) => (i ? COMPANY_ROLES[c.role].label.toLowerCase() : COMPANY_ROLES[c.role].label))
      .join(" and ");

    cards.push(
      card({
        path: companyPath(role, slug),
        title: escapeHtml(name),
        more: `All ${total} →`,
        moreLabel: `All ${total} games by ${escapeHtml(name)}`,
        blurb: `${as} of ${escapeHtml(displayName(game))}`,
        games: cardGames(apps, changeNote)
      })
    );
  }

  if (!cards.length) return "";

  return `
      <section class="more-from">
        <div class="section-head">
          <h2>More from the same ${cards.length > 1 ? "studios" : "studio"}</h2>
        </div>
        <ol class="tag-cards">${cards.join("")}</ol>
      </section>`;
}

// A game's whole page under `back` (the link back to the list), as app.js
// first draws it and the Worker writes it: the chart panel still to be
// filled, then everything the app's own response says. The toggles and
// which kinds are listed are the reader's own in app.js, the defaults in
// the Worker.
export function gamePage(
  game: AppResponse,
  name: string,
  {
    back,
    range,
    shownKinds = new Set(DEFAULT_UPDATE_KINDS),
    showSales = true,
    listedKinds = DEFAULT_UPDATE_KINDS,
    isShaded = () => false
  }: {
    back: string;
    range: HistoryRange;
    shownKinds?: ReadonlySet<string>;
    showSales?: boolean;
    listedKinds?: Iterable<string>;
    isShaded?: (region: Region) => boolean;
  }
): string {
  return `
      ${back}
      ${appHead(game, name)}
      ${chartPanel(game, name, { range, shownKinds, showSales, isShaded })}
      ${appProse(game, name)}
      ${insights(game, range)}
      ${updateList(game, name, listedKinds)}
      ${gameLists(game, name)}
      ${embedPanel(game.app_id, name)}`;
}

// What the figures say about the game, beyond the figures themselves, as
// a panel of cards: when its players play, and the update that moved them
// most. Nothing for a game with nothing to say yet.
function insights(game: AppResponse, range: HistoryRange): string {
  const cards = [playTimeCard(game, range), updateCard(game)].filter(Boolean);
  if (!cards.length) return "";

  return `
      <section class="insights" aria-labelledby="insights-heading">
        <div class="section-head">
          <div>
            <h2 id="insights-heading">Insights</h2>
            <p class="muted">What the hourly counts say about when it's played and how updates land.</p>
          </div>
        </div>
        <div class="insight-cards">${cards.join("")}</div>
      </section>`;
}

// The top of a game's page: its art, name, the store's line about it, its
// store facts, and how long it's been tracked.
function appHead(game: AppResponse, name: string): string {
  return `
      <div class="app-head">
        ${art(game, name)}
        <div>
          <h1>${escapeHtml(name)}</h1>
          ${moverBadges(game)}
          ${
          game.store?.description
            ? `<p class="store-description">${escapeHtml(game.store.description)}</p>`
            : ""
        }
          <div class="muted app-tracking">
            ${game.active ? "" : "No longer tracked · "}
            ${game.tracking_since ? `Tracking since ${date(game.tracking_since)} · ` : ""}
            ${externalLink(storeUrl(game.app_id), "Steam store page")}
          </div>
        </div>
        ${storeFacts(game.store, game.companies, game.price ?? null)}
      </div>`;
}

// A game page's figures in sentences. Everything here is also on the page as
// numbers; the prose is what a search engine or an assistant can quote, so
// the first sentence stands on its own: the game, the count, what it counts
// and the hour it was read. The peak is only ever "recorded here": it starts
// when tracking did, so it isn't Steam's all-time record.
function appSummary(game: AppResponse, name: string): string {
  const title = escapeHtml(name);

  // A count always comes with the hour it was read.
  if (game.player_count == null || game.recorded_at == null) {
    return `<p>No player count has been recorded for ${title} yet. Counts are
        taken every hour, so check back soon.</p>`;
  }

  let now = `${title} had <strong>${number.format(game.player_count)}</strong>
      concurrent ${plural(game.player_count, "player")} on Steam at
      the latest hourly reading, ${hour(game.recorded_at)}`;

  const before = game.day_ago_player_count;
  const change = dayChange(game.day_change);
  // A change always comes with the count it's from.
  if (change && before != null) {
    now +=
      change.way === "flat"
        ? `, about the same as a day earlier (${number.format(before)})`
        : `, ${change.way} ${percentPlain[change.digits].format(
            Math.abs(change.ratio)
          )} from ${number.format(before)} a day earlier`;
  }

  // A rank always comes with its ranking's hour and size.
  if (game.rank != null && game.as_of != null && game.ranked_total != null) {
    const move = rankMove(game);
    const moved = !move
      ? ""
      : move.way === "flat"
        ? ", the same place as a day earlier"
        : `, ${move.way} ${move.places} ${plural(move.places, "place")} from
             #${game.day_ago_rank} a day earlier`;

    // The ranking's own hour, which is later than recorded_at for an app the
    // crawler missed.
    now += `. As of ${hour(game.as_of)}, it ranks
        <strong>#${game.rank}</strong> of the
        ${number.format(game.ranked_total)} games tracked here${moved}.`;
  } else {
    now += ".";
  }

  let peak = "";
  if (game.peak_player_count != null && game.peak_recorded_at != null) {
    const since = game.tracking_since
      ? ` since tracking began on ${date(game.tracking_since)}`
      : "";

    const share = peakShare(game.player_count, game.peak_player_count);

    if (share == null) {
      peak = `<p>That latest reading is the highest count recorded here${since}.</p>`;
    } else {
      peak = `<p>The highest count recorded here${since} is
          <strong>${number.format(game.peak_player_count)}</strong>, on
          ${date(game.peak_recorded_at)}; the latest reading is
          ${share === 0 ? "under 1" : share}% of that.</p>`;
    }
  }

  // From the weekly tag: the evenings the busiest hours fall in, said as
  // when people play, which is all an hour of the day can show.
  let when = "";
  const { evenings, peakHour } = playTimes(game.tags);
  if (evenings) {
    const places = evenings.map(
      (region) =>
        `<a href="${tagPagePath("busy_hours", region)}" data-nav>${EVENINGS[region].place}</a>`
    );
    const busiest =
      peakHour == null ? "" : `, with the busiest hour around ${clock(peakHour)}`;

    when = places.length
      ? `<p>Over the last four weeks, ${title} has been busiest during the
           evening in ${places.join(" and ")}${busiest}. That's when its
           players play, not necessarily where they live.</p>`
      : `<p>Over the last four weeks, ${title}'s player count has held fairly
           steady <a href="${tagPagePath("busy_hours", AROUND_THE_CLOCK)}" data-nav>around
           the clock</a>${busiest}.</p>`;
  }

  const stopped = game.active
    ? ""
    : `<p>${title} is no longer tracked, so these figures stop at its last
         reading.</p>`;

  return `<p>${now}</p>${peak}${when}${stopped}`;
}

// The summary under a heading that asks what people search for.
function appProse(game: AppResponse, name: string): string {
  return `
      <section class="prose">
        <h2>How many people are playing ${escapeHtml(name)}?</h2>
        ${appSummary(game, name)}
      </section>`;
}

// The games ranked either side of this one (the API's window of ranks, this
// game's row included), as a card like the studios' beside it, linking to
// the list page they're on: a way on to similar-sized games for readers and
// a path between game pages for crawlers.
function nearbyList(game: AppResponse, name: string): string {
  const first = game.nearby?.[0];
  const last = game.nearby?.at(-1);
  if (!game.nearby || game.nearby.length < 2 || !first || !last || game.rank == null) return "";

  const path = pageHref(Math.floor((game.rank - 1) / PER_PAGE));

  return `
      <section class="nearby">
        <div class="section-head"><h2>Games ranked near ${escapeHtml(name)}</h2></div>
        <ol class="tag-cards">${card({
        path,
        title: `Ranks ${first.rank}–${last.rank}`,
        more: "Full ranking →",
        moreLabel: "The full ranking",
        blurb: "By concurrent players at the latest hour",
        games: cardGames(game.nearby, changeNote, game.app_id)
      })}</ol>
      </section>`;
}

// The games ranked near this one and those from the same studios, side by
// side where there's room for both (see .game-lists in style.css): both are
// short lists of games. Either alone takes the width.
function gameLists(game: AppResponse, name: string): string {
  const lists = [nearbyList(game, name), companyCards(game)].filter(Boolean);
  if (lists.length < 2) return lists.join("");

  return `<div class="game-lists">${lists.join("")}</div>`;
}
